import { Fragment, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogOut, Menu, X } from 'lucide-react';

import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/Button';
import { FeatureNotEnabled } from '@/components/FeatureNotEnabled';
import {
  disabledNavOptionAt,
  isNavOptionAvailable,
  navLabel,
  type NavOption,
} from '@/constants/navigation';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { cn } from '@/lib/utils';

/**
 * The shell every signed-in console is drawn in: sidebar, top bar, content.
 *
 * There were three copies of this — the institute's, the platform's, and a
 * fourth hand-rolled inside the teacher's dashboard page — and they had already
 * drifted: one sidebar was 236px and another 250px, one scrolled its links and
 * the others did not, one closed the drawer on navigation and one did not.
 * Three copies of a shell is three places to fix the next thing wrong with it
 * (CLAUDE.md §3).
 *
 * **Only the menu and the mark differ per role**, which is the whole point: a
 * console passes its own items, its own mark and whatever belongs in its top
 * bar, and gets the same frame — and the same indigo rail — as everybody else.
 * The platform console used to ask for a near-black skin of its own; it is
 * drawn like the rest now, and what tells two open tabs apart is the mark and
 * the title, not a second colour scheme. Nothing about which role is reading is
 * decided here beyond hiding what they may not open.
 */

export interface ConsoleLayoutProps {
  /** This panel's menu, from `constants/navigation`. */
  nav: readonly NavOption[];
  /** The mark and the two lines beside it, top-left of the sidebar. */
  brand: { mark: ReactNode; title: string; subtitle: string };
  /**
   * Who is signed in, as the foot of the sidebar names them.
   *
   * Optional: the institute consoles now name the person in the top bar
   * (`ConsoleIdentity`) instead, so their sidebar carries the sign-out button
   * alone. Pass it and the foot grows the two lines back.
   */
  account?: { name: string; meta: string };
  /**
   * A block between the mark and the menu — the parent panel's child switcher.
   *
   * Hidden while the rail is collapsed, where there is no width for it. Drawn
   * on the sidebar's own dark sheet, so whatever is passed has to be painted
   * for that rather than for a page surface.
   */
  sidebarTop?: ReactNode;
  onSignOut?: () => void | Promise<void>;
  /** Sits on the left of the top bar from `lg` up, where there is room for it. */
  greeting?: ReactNode;
  /** Pinned to the right of the top bar, before the theme toggle. */
  actions?: ReactNode;
}

/**
 * The frame's paint, in one table.
 *
 * Kept together rather than scattered down the markup, so that the sidebar's
 * colours are read and changed in one place rather than hunted for among the
 * layout classes.
 */
const SKIN = {
  aside:
    'bg-[radial-gradient(circle_at_10%_76%,rgba(97,71,221,.52),transparent_24%),linear-gradient(155deg,#15123f_0%,#20175d_60%,#171144_100%)] border-r border-white/10 shadow-lg',
  brandBar: 'border-b border-white/10 px-3 py-3.5',
  brandMark:
    'h-8 w-8 rounded-xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.35)] ring-1 ring-white/30',
  brandTitle: 'text-sm font-extrabold tracking-tight text-white',
  brandSubtitle: 'text-[11px] font-medium text-sky-300',
  close: 'rounded-lg text-slate-400 hover:bg-white/10 hover:text-white',
  navList: 'px-3 pb-3 pt-4',
  navGroup: 'px-3 pb-1 pt-1 text-2xs font-extrabold uppercase tracking-label text-slate-400',
  navItem: 'gap-2.5 rounded-xl border px-3 py-2.5 text-[14px] font-semibold active:scale-[0.97]',
  navActive: 'border-white/25 bg-white/15 font-extrabold text-white shadow-md backdrop-blur-md',
  navIdle: 'border-transparent text-slate-200/90 hover:bg-white/10 hover:text-white',
  footer: 'border-t border-white/10 p-3',
  accountName: 'text-white',
  accountMeta: 'text-slate-300',
  signOut:
    'flex w-full items-center justify-center gap-2.5 rounded-xl border border-white/20 bg-white/10 py-2.5 text-[14px] font-bold text-white hover:border-rose-400/40 hover:bg-rose-500/25 hover:text-rose-200',
  signOutIcon: 'text-rose-300',
  overlay: 'bg-black/60 backdrop-blur-xs',
  header: 'sticky top-0 z-30 border-b border-line bg-surface shadow-2xs',
} as const;

export function ConsoleLayout({
  nav,
  brand,
  account,
  sidebarTop,
  onSignOut,
  greeting,
  actions,
}: ConsoleLayoutProps) {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const features = useAuthStore((state) => state.user?.features);
  const featureNames = useAuthStore((state) => state.user?.featureNames);
  const mustChangePassword = useAuthStore((state) => state.user?.mustChangePassword);
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('console_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('console_sidebar_collapsed', String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  // Entries for a feature the school has not got switched on are left off. The
  // platform console's menu names no features, so it is never affected.
  const visibleItems = nav.filter((item) => isNavOptionAvailable(item, permissions, features));
  // A bookmark into a switched-off feature lands on a notice, not on a page
  // whose every request the server would refuse.
  const closed = disabledNavOptionAt(nav, location.pathname, features);
  // An account made to replace its password is always let through to do it,
  // whatever the school has switched off — or it could never get any further.
  const disabled = closed?.feature === 'CHANGE_PASSWORD' && mustChangePassword ? null : closed;
  // The landing page switched off sends everybody on to the first page they
  // can open, rather than greeting every sign-in with a notice.
  const landing = disabled && disabled === nav[0] ? visibleItems[0] : undefined;

  return (
    <div
      className={cn(
        'min-h-dvh bg-paper lg:grid transition-all duration-300 w-full max-w-full overflow-x-clip print:block print:w-full print:max-w-none print:min-h-0 print:overflow-visible',
        isCollapsed ? 'lg:grid-cols-[72px_minmax(0,1fr)]' : 'lg:grid-cols-[235px_minmax(0,1fr)]',
      )}
    >
      {/* ---------------- sidebar ---------------- */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 print:hidden',
          isCollapsed ? 'w-[235px] lg:w-[72px]' : 'w-[235px]',
          SKIN.aside,
          isNavOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div
          className={cn(
            'flex shrink-0 items-center justify-between',
            SKIN.brandBar,
            isCollapsed && 'lg:flex-col lg:gap-2.5 lg:px-2 lg:py-3.5',
          )}
        >
          {isCollapsed ? (
            <>
              {/* Mobile drawer header */}
              <div className="flex items-center gap-3 lg:hidden min-w-0">
                <span className={cn('grid shrink-0 place-items-center overflow-hidden', SKIN.brandMark)}>
                  {brand.mark}
                </span>
                <div className="min-w-0">
                  <span className={cn('block truncate leading-tight', SKIN.brandTitle)}>{brand.title}</span>
                  <span className={cn('flex items-center gap-1.5 truncate', SKIN.brandSubtitle)}>{brand.subtitle}</span>
                </div>
              </div>

              {/* Desktop collapsed header: logo & expand button */}
              <span
                className={cn('hidden lg:grid shrink-0 place-items-center overflow-hidden', SKIN.brandMark)}
                title={brand.title}
              >
                {brand.mark}
              </span>
              <button
                type="button"
                onClick={toggleCollapsed}
                aria-label="Expand sidebar"
                title="Expand sidebar"
                className="hidden lg:flex h-8 w-8 items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-md shadow-blue-500/30 transition-all cursor-pointer active:scale-95"
              >
                <ChevronRight className="h-4 w-4" />
              </button>

              {/* Mobile close button */}
              <button
                type="button"
                onClick={() => setIsNavOpen(false)}
                aria-label="Close menu"
                className={cn('p-1.5 transition-colors lg:hidden', SKIN.close)}
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className={cn(
                    'grid shrink-0 place-items-center overflow-hidden',
                    SKIN.brandMark,
                  )}
                  aria-hidden
                >
                  {brand.mark}
                </span>
                <div className="min-w-0">
                  <span className={cn('block truncate leading-tight', SKIN.brandTitle)}>
                    {brand.title}
                  </span>
                  <span className={cn('flex items-center gap-1.5 truncate', SKIN.brandSubtitle)}>
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400 animate-pulse" aria-hidden />
                    <span className="truncate">{brand.subtitle}</span>
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {/* Desktop collapse button */}
                <button
                  type="button"
                  onClick={toggleCollapsed}
                  aria-label="Collapse sidebar"
                  title="Collapse sidebar"
                  className="hidden lg:flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-500 text-white shadow-xs transition-all cursor-pointer active:scale-95 shrink-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {/* Mobile close button */}
                <button
                  type="button"
                  onClick={() => setIsNavOpen(false)}
                  aria-label="Close menu"
                  className={cn('p-1.5 transition-colors lg:hidden', SKIN.close)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </div>

        {sidebarTop && (
          <div className={cn('shrink-0 px-3 pt-3', isCollapsed && 'lg:hidden')}>{sidebarTop}</div>
        )}

        {/* Only the links scroll when the menu outgrows the screen, so the mark
            above and the account below stay put. */}
        <nav
          className={cn(
            'custom-scrollbar flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto',
            SKIN.navList,
            isCollapsed && 'lg:px-2 lg:items-center',
          )}
        >
          {visibleItems.map((item, index) => {
            const { key, to, icon: Icon, end, group, matchPaths } = item;
            // The catalogue's one name for the page, so the menu, the Roles
            // screen and the platform console never disagree.
            const label = navLabel(item, featureNames);
            const isMatch = (targetPath: string) =>
              location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);
            const isActive =
              (end ? location.pathname === to : isMatch(to)) ||
              Boolean(matchPaths?.some((p) => isMatch(p)));

            return (
              <Fragment key={key}>
                {/* Drawn once, above the first link that carries it. A heading
                    the collapsed rail has no width for is dropped rather than
                    wrapped — the icons still read in the order the groups put
                    them in. */}
                {group && group !== visibleItems[index - 1]?.group && (
                  <p
                    className={cn(
                      SKIN.navGroup,
                      index > 0 && 'mt-3',
                      isCollapsed && 'lg:hidden',
                    )}
                  >
                    {group}
                  </p>
                )}
                <NavLink
                  to={to}
                  end={end}
                  title={label}
                  // The drawer is the whole screen on a phone; leaving it open
                  // over the page somebody just asked for hides what they came
                  // to see.
                  onClick={() => setIsNavOpen(false)}
                  className={cn(
                    'group relative flex items-center',
                    SKIN.navItem,
                    isCollapsed ? 'lg:justify-center lg:px-2 lg:py-2.5 lg:w-10 lg:h-10' : '',
                    isActive ? SKIN.navActive : SKIN.navIdle,
                    'transition-all duration-200 ease-out',
                    !isActive && !isCollapsed && 'hover:translate-x-0.5',
                  )}
                >
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0 transition-transform group-hover:scale-110',
                      isActive
                        ? 'text-white drop-shadow-[0_0_6px_rgba(255,255,255,0.6)]'
                        : 'text-slate-400 group-hover:text-slate-200',
                    )}
                    strokeWidth={isActive ? 2.2 : 1.8}
                  />
                  <span className={cn('truncate', isCollapsed && 'lg:hidden')}>{label}</span>
                </NavLink>
              </Fragment>
            );
          })}
        </nav>

        {(account || onSignOut) && (
          <div className={cn('shrink-0', SKIN.footer, isCollapsed && 'lg:p-2 lg:flex lg:justify-center')}>
            {account && !isCollapsed && (
              <>
                <p className={cn('truncate text-sm font-[560]', SKIN.accountName)}>{account.name}</p>
                <p className={cn('truncate text-xs', SKIN.accountMeta)}>{account.meta}</p>
              </>
            )}

            {onSignOut && (
              <button
                type="button"
                onClick={() => void onSignOut()}
                title="Sign out"
                aria-label="Sign out"
                className={cn(
                  'transition-all duration-200 cursor-pointer',
                  account && !isCollapsed && 'mt-3',
                  SKIN.signOut,
                  isCollapsed && 'lg:w-10 lg:h-10 lg:p-0 lg:justify-center',
                )}
              >
                <LogOut className={cn('h-4 w-4 shrink-0', SKIN.signOutIcon)} />
                <span className={cn(isCollapsed && 'lg:hidden')}>Sign out</span>
              </button>
            )}
          </div>
        )}
      </aside>

      {/* Dimmer behind the drawer on small screens. */}
      {isNavOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setIsNavOpen(false)}
          className={cn('fixed inset-0 z-40 lg:hidden', SKIN.overlay)}
        />
      )}

      {/* ---------------- content ---------------- */}
      <div className="flex min-h-dvh flex-col min-w-0 w-full max-w-full overflow-x-clip print:block print:w-full print:max-w-none print:min-h-0 print:overflow-visible">
        <header
          className={cn('relative z-40 flex items-center justify-between gap-3 px-4 py-3 lg:px-6 print:hidden', SKIN.header)}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsNavOpen(true)}
            leftIcon={<Menu className="h-4 w-4 text-accent" />}
            className="lg:hidden"
          >
            Menu
          </Button>

          {greeting}

          {/* Pinned right as one group, so the bar reads as a set of controls
              rather than being spread across the width by `justify-between`. */}
          <div className="ml-auto flex items-center gap-2">
            {actions}
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 px-4 pt-3 pb-6 lg:px-6 lg:pt-4 print:p-0 print:m-0 print:block print:w-full print:max-w-none print:overflow-visible min-w-0 w-full max-w-full overflow-x-clip">
          <div className="relative flex w-full max-w-full min-w-0 flex-col gap-4 sm:gap-5 print:block print:max-w-none print:w-full print:gap-3 overflow-x-clip print:overflow-visible">
            {landing ? (
              <Navigate to={landing.to} replace />
            ) : disabled ? (
              <FeatureNotEnabled label={navLabel(disabled, featureNames)} />
            ) : (
              <Outlet />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
